import { CheckIcon, ExclamationTriangleIcon, StopIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ActivitySection } from '../../lib/conversationPresentation';
import type { TurnSummary } from '../../utils/coworkTurnSummary';
import type { CoworkMessage } from '../../types/cowork';
import { stripThinkingLeak } from '../../utils/thinkingLeak';
import { i18nService } from '../../services/i18n';
import { SessionCreditLabel, type SessionCreditSnapshot } from '../account/SessionCredit';
import { CopyButton, ResultFeedbackButtons } from './ConversationResultActions';

/**
 * 把 epoch 毫秒渲染成 `HH:MM GMT+8`。
 *
 * 一律用**观看者本地时区**，并显式带出时区名：服务端现在只落 epoch，不再把时刻烤进
 * 文案，所以沙箱状态记录与任务完成 pill 都走这里，口径天然一致；带上时区可以避免
 * 跨时区看同一条记录时对不上号。
 */
function formatClockWithZone(epochMs: number): string {
  const d = new Date(epochMs);
  if (!Number.isFinite(d.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('zh-CN', {
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZoneName: 'short',
    }).formatToParts(d);
    const hh = parts.find(p => p.type === 'hour')?.value ?? '';
    const mm = parts.find(p => p.type === 'minute')?.value ?? '';
    const tz = parts.find(p => p.type === 'timeZoneName')?.value ?? '';
    if (!hh || !mm) return '';
    return tz ? `${hh}:${mm} ${tz}` : `${hh}:${mm}`;
  } catch {
    return '';
  }
}

/** `HH:MM GMT+8 · 用时 Ns · 已完成`；时刻解析不出时只显示用时，绝不出现 Invalid Date。 */
export function formatCompletionMeta(completedAt: number, elapsedMs: number, label: string): string {
  const clock = formatClockWithZone(completedAt);
  const sec = Math.max(0, Math.round((Number.isFinite(elapsedMs) ? elapsedMs : 0) / 1000));
  const used = sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m${sec % 60}s`;
  return clock ? `${clock} · ${i18nService.t("turnDuration")} ${used} · ${label}` : `${i18nService.t("turnDuration")} ${used} · ${label}`;
}


/** 每轮统一结果底栏；复制完整公开结果，反馈绑定最终主回答，无耗时记录也能读权威积分。 */
export default function ConversationTurnFooter({ turnId, sessionId, desktop, sections, summary, credit,
  turnIds, running, showActions, mapDisplayText }: {
  turnId: string; sessionId: string; desktop: boolean; sections: ActivitySection[];
  summary?: TurnSummary; credit: SessionCreditSnapshot | null;
  /** 用户提问记录上的计费轮次标识，可能包含失败重试的多次执行。 */
  turnIds: unknown; running: boolean; showActions: boolean; mapDisplayText?: (text: string) => string;
}) {
  if (running) return null;
  const answers: Array<{ message: CoworkMessage; content: string }> = [];
  for (const section of sections) {
    if (section.process) continue;
    for (const { item } of section.entries) {
      if (item.type !== 'assistant' || item.message.metadata?.parentToolUseId
        || item.message.metadata?.isThinking || item.message.metadata?.isStreaming) continue;
      const raw = item.message.content;
      const content = stripThinkingLeak(mapDisplayText ? mapDisplayText(raw) : raw).trim();
      if (content) answers.push({ message: item.message, content });
    }
  }
  const result = answers[answers.length - 1]?.message;
  // 旧工具轮次可能没有正文/耗时，持久化的计费ID仍须保留费用入口。
  const hasBillingTurn = Array.isArray(turnIds) && turnIds.some(id => typeof id === 'string' && id.trim());
  if (!result && !summary && !hasBillingTurn) return null;
  const status = summary?.terminalStatus;
  const Icon = status === 'completed' ? CheckIcon : status === 'failed' ? XMarkIcon
    : status === 'stopped' ? StopIcon : ExclamationTriangleIcon;
  return <footer data-turn-footer={turnId} className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
    {showActions && result && <div className="flex min-w-0 flex-wrap items-center gap-1.5" data-export-exclude>
      <CopyButton content={answers.map(answer => answer.content).join('\n\n')} visible />
      <ResultFeedbackButtons key={`${sessionId}:${result.id}`} sessionId={sessionId} message={result} desktop={desktop} />
    </div>}
    <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-1">
      {summary && <span title={summary.reason} className="inline-flex items-center gap-1.5">
        <Icon aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 ${status === 'completed' ? 'text-emerald-500' : status === 'failed' ? 'text-red-500' : 'text-amber-500'}`} />
        <span>{formatCompletionMeta(summary.completedAt, summary.durationMs, i18nService.t(`turnStatus_${status}`))}</span>
      </span>}
      <SessionCreditLabel snapshot={credit} turnIds={turnIds} />
    </div>
  </footer>;
}
